import React, { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { ConfigProvider } from '../context/ConfigContext';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import VerifyEmailPage from '../pages/VerifyEmailPage';
import PricingPage from '../pages/PricingPage';
import { apiGet } from '../api';

/**
 * AuthGate — fetches server config, enforces auth + subscription gates,
 * then renders AppComponent when all checks pass.
 *
 * @param {{ AppComponent: React.ComponentType }} props
 */
export default function AuthGate({ AppComponent }) {
  const { user, loading: authLoading } = useAuth();
  const [serverConfig, setServerConfig]       = useState(null);
  const [configError, setConfigError]         = useState(null);
  const [hasSubscription, setHasSubscription] = useState(null);
  const [authPage, setAuthPage]               = useState('login');

  useEffect(() => {
    apiGet('/config')
      .then(cfg => setServerConfig(cfg))
      .catch((err) => {
        const host = window.location.hostname;
        const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '::1';

        if (isLocalHost) {
          setServerConfig({ saasAuthEnabled: false, billingEnabled: false });
          return;
        }

        console.error('[AuthGate] Failed to load server config:', err);
        setConfigError(err);
      });
  }, []);

  const saasAuthEnabled = serverConfig?.saasAuthEnabled ?? false;
  const billingEnabled  = serverConfig?.billingEnabled  ?? false;

  useEffect(() => {
    if (!saasAuthEnabled || !billingEnabled || !user?.emailVerified) {
      setHasSubscription(true);
      return;
    }
    setHasSubscription(null);
    apiGet('/billing/status')
      .then(({ hasAccess }) => setHasSubscription(hasAccess))
      .catch(() => setHasSubscription(true));
  }, [user, saasAuthEnabled, billingEnabled]);

  const stillLoading = authLoading || (!configError && serverConfig === null) ||
    (user?.emailVerified && saasAuthEnabled && billingEnabled && hasSubscription === null);

  if (stillLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: '100vh', background: theme.bg, color: theme.textMuted, fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  if (configError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minHeight: '100vh', background: theme.bg, color: theme.text, padding: 24 }}>
        <div style={{ maxWidth: 520, border: `1px solid ${theme.border}`, borderRadius: 12,
                      background: theme.panel, padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
          <h1 style={{ margin: '0 0 12px', fontSize: 22 }}>Authentication configuration unavailable</h1>
          <p style={{ margin: '0 0 18px', color: theme.textMuted, lineHeight: 1.5 }}>
            FusionQuery could not load its server configuration. Access is blocked until
            authentication settings are available.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ border: `1px solid ${theme.primary}`, background: theme.primary,
                     color: '#fff', borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!saasAuthEnabled) {
    return (
      <ConfigProvider serverConfig={serverConfig}>
        <AppComponent serverConfig={serverConfig} />
      </ConfigProvider>
    );
  }

  if (!user) {
    return authPage === 'register'
      ? <RegisterPage onLogin={() => setAuthPage('login')} />
      : <LoginPage onRegister={() => setAuthPage('register')} />;
  }

  if (!user.emailVerified) {
    return <VerifyEmailPage />;
  }

  if (billingEnabled && !hasSubscription) {
    return <PricingPage onSubscribed={() => setHasSubscription(true)} />;
  }

  return (
    <ConfigProvider serverConfig={serverConfig}>
      <AppComponent serverConfig={serverConfig} />
    </ConfigProvider>
  );
}
